/* MCAMasterDataPanel — shared rich view of MCA Company Information, Directors,
 * Charges, Director Other Companies (Section 167(1) Impact Map), and Documents
 * Folder URL field.
 *
 * Used by both CCFS (`lead` is a ccfs_lead document) and Micro CFO Client Workspace
 * (`lead` is a client document with the same MCA fields).
 *
 * Props:
 *   lead        — record carrying mca_company_info, mca_directors, mca_charges,
 *                 director_other_companies, documents_folder_url, cin_info,
 *                 mca_cin_match, small_company_analysis, etc.
 *   onRefresh   — refetch handler
 *   apiBase     — '/ccfs-leads' (default) or '/clients'; used by the Documents
 *                 Folder save and by nested components
 *   DinStatusCell — optional component (lead, director, idx, onRefresh) → JSX.
 *                   When omitted, the DIN Status column is hidden.
 */
import { Building2, Users, Landmark, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { EditSaveField } from './components/EditSaveField';
import { DirectorOtherCompanies } from './sections/DirectorOtherCompanies';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MCAMasterDataPanel({
  lead,
  onRefresh,
  apiBase = '/ccfs-leads',
  DinStatusCell = null,
}) {
  if (!lead?.mca_company_info) {
    return (
      <p className="text-xs text-slate-400">
        No MCA data uploaded yet. Upload the Excel file from MCA portal to auto-populate company data.
      </p>
    );
  }
  const showDinCol = !!DinStatusCell;

  return (
    <div className="space-y-4">
      {/* Company Information */}
      <div>
        <p className="text-xs font-semibold text-[#1e3a5f] mb-2 flex items-center gap-1.5">
          <Building2 className="w-3.5 h-3.5" /> Company Information
        </p>

        {/* Company Type derived from CIN — prominent row, single source of truth */}
        {lead.cin_info?.valid && (
          <div
            className="mb-3 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 flex items-center gap-2"
            data-testid={`mca-company-type-${lead.id}`}
          >
            <span className="text-[10px] text-blue-700 uppercase font-bold tracking-wide min-w-[140px] shrink-0">
              Company Type (from CIN)
            </span>
            <span className="text-xs text-blue-900 font-bold">
              {lead.cin_info.company_type_label || 'Unknown'}
              {lead.cin_info.listing_status && (
                <span className="text-blue-700 font-medium"> · {lead.cin_info.listing_status}</span>
              )}
              {lead.cin_info.is_section_8 && (
                <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-300 font-bold">
                  Sec 248(3) strike-off NOT permitted
                </span>
              )}
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5">
          {Object.entries(lead.mca_company_info)
            .filter(([k]) => k !== 'company_information')
            .map(([key, val]) => (
              <div key={key} className="flex gap-2 py-1 border-b border-slate-100 last:border-0">
                <span className="text-[10px] text-slate-400 uppercase min-w-[140px] shrink-0">
                  {key.replace(/_/g, ' ')}
                </span>
                <span className="text-xs text-slate-800 font-medium">{val || '—'}</span>
              </div>
            ))}
        </div>
      </div>

      {/* Directors */}
      {lead.mca_directors?.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-[#1e3a5f] mb-2 flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> Directors / Signatories ({lead.mca_directors.length})
          </p>
          {showDinCol && (() => {
            const unverifiedCount = (lead.mca_directors || []).filter(
              (d) => !d.din_status || d.din_status === 'not_verified'
            ).length;
            if (unverifiedCount === 0) return null;
            return (
              <div
                className="mb-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2"
                data-testid={`din-verify-banner-${lead.id}`}
              >
                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-[11px] font-bold text-amber-800">
                    Manual verification required — {unverifiedCount} of {lead.mca_directors.length}{' '}
                    director(s) pending DIN status check
                  </p>
                  <p className="text-[10px] text-amber-700 mt-0.5">
                    MCA Master Excel does not carry live DIN status. Open{' '}
                    <a
                      href="https://www.mca.gov.in/mcafoportal/viewSignatoryDetails.do"
                      target="_blank"
                      rel="noreferrer"
                      className="underline font-medium"
                    >
                      MCA DIN Services
                    </a>{' '}
                    for each DIN and update the status below (Active / Deactivated / Disqualified u/s 164 /
                    Surrendered).
                  </p>
                </div>
              </div>
            );
          })()}
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="text-left px-3 py-2 text-[10px] text-slate-500 font-semibold">DIN/PAN</th>
                  <th className="text-left px-3 py-2 text-[10px] text-slate-500 font-semibold">Name</th>
                  <th className="text-left px-3 py-2 text-[10px] text-slate-500 font-semibold">Designation</th>
                  <th className="text-left px-3 py-2 text-[10px] text-slate-500 font-semibold">Category</th>
                  <th className="text-left px-3 py-2 text-[10px] text-slate-500 font-semibold">Appointed</th>
                  <th className="text-left px-3 py-2 text-[10px] text-slate-500 font-semibold">Cessation</th>
                  {showDinCol && (
                    <th className="text-left px-3 py-2 text-[10px] text-slate-500 font-semibold">DIN Status</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {lead.mca_directors.map((d, i) => (
                  <tr
                    key={d.din_pan || d['din/pan'] || `dir-${i}`}
                    className="border-b last:border-0 hover:bg-slate-50/50"
                  >
                    <td className="px-3 py-2 font-mono text-slate-600">{d.din_pan || d['din/pan'] || '—'}</td>
                    <td className="px-3 py-2 font-medium text-slate-800">{d.name || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{d.designation || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{d.category || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{d.date_of_appointment || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{d.cessation_date || '—'}</td>
                    {showDinCol && (
                      <td className="px-3 py-2">
                        <DinStatusCell lead={lead} director={d} idx={i} onRefresh={onRefresh} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Charges */}
      <div>
        <p className="text-xs font-semibold text-[#1e3a5f] mb-2 flex items-center gap-1.5">
          <Landmark className="w-3.5 h-3.5" /> Index of Charges ({lead.mca_charges?.length || 0})
        </p>
        {lead.mca_charges?.length > 0 ? (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="text-left px-3 py-2 text-[10px] text-slate-500 font-semibold">Charge Holder</th>
                  <th className="text-left px-3 py-2 text-[10px] text-slate-500 font-semibold">Amount</th>
                  <th className="text-left px-3 py-2 text-[10px] text-slate-500 font-semibold">Created</th>
                  <th className="text-left px-3 py-2 text-[10px] text-slate-500 font-semibold">Modified</th>
                  <th className="text-left px-3 py-2 text-[10px] text-slate-500 font-semibold">Satisfied</th>
                </tr>
              </thead>
              <tbody>
                {lead.mca_charges.map((c, i) => (
                  <tr
                    key={`${c.charge_holder_name}-${c.date_of_creation}-${i}`}
                    className="border-b last:border-0 hover:bg-slate-50/50"
                  >
                    <td className="px-3 py-2 font-medium text-slate-800">{c.charge_holder_name || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{c.amount || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{c.date_of_creation || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{c.date_of_modification || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{c.date_of_satisfaction || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-xs text-slate-400 bg-slate-50 rounded-lg px-3 py-2 border border-slate-100">
            No charges on record
          </p>
        )}
      </div>

      {/* Director Other Companies — Section 167(1) Impact Map */}
      <DirectorOtherCompanies lead={lead} onRefresh={onRefresh} apiBase={apiBase} />

      {/* Documents Folder URL */}
      <div>
        <EditSaveField
          label="Documents Folder"
          value={lead.documents_folder_url || ''}
          type="url"
          placeholder="Paste Google Drive folder link..."
          onSave={async (val) => {
            const token = localStorage.getItem('ratnatraya_token');
            await fetch(`${API}/api${apiBase}/${lead.id}`, {
              method: 'PATCH',
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ documents_folder_url: val }),
            });
            toast.success('Folder link saved');
            onRefresh?.();
          }}
          testId={`docs-folder-url-${lead.id}`}
          linkButton={
            lead.documents_folder_url
              ? { href: lead.documents_folder_url, label: 'Open Folder' }
              : null
          }
        />
        {lead.documents_folder_url &&
          (lead.documents_folder_url_created_at || lead.documents_folder_url_updated_at) && (
            <p className="text-[10px] text-slate-400 mt-1" data-testid={`docs-folder-timestamp-${lead.id}`}>
              {lead.documents_folder_url_created_at && (
                <>
                  Created:{' '}
                  {new Date(lead.documents_folder_url_created_at).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </>
              )}
              {lead.documents_folder_url_updated_at &&
                lead.documents_folder_url_updated_at !== lead.documents_folder_url_created_at && (
                  <>
                    {' · '}Updated:{' '}
                    {new Date(lead.documents_folder_url_updated_at).toLocaleDateString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </>
                )}
            </p>
          )}
      </div>

      {lead.mca_uploaded_at && (
        <p className="sr-only">Uploaded: {new Date(lead.mca_uploaded_at).toLocaleString('en-IN')}</p>
      )}
    </div>
  );
}
